/**
 * A React component that shows relevant Fulfillment Groups for the time period and provides quick access links to various useful resources
 *
 * @param {Object} props - The properties object.
 * @param {string} props.dateStart - The start date of the week to query for, formatted as 'YYYY-MM-DD'.
 * @param {string} props.dateEnd - The end date of the week to query for, formatted as 'YYYY-MM-DD'.
 *
 * @returns {JSX.Element} - A React component that contains:
 *  A button which opens a dialog that displays a datagrid with fulfillment group information including: FG ID, FG Name (which links to FG page in Kuebiko),
 * Customer Name, Delivery IDs (which can be copied to clipboard), Delivery Regions (which link to Country pages in Kuebiko),
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import GaneshDataGrid from "../../../components/DataGrid/GaneshDataGrid";
import GaneshCopyToClipboardBtn from "../../../components/UI/GaneshCopyToClipboard";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import { RiExternalLinkLine } from "@remixicon/react";
import { FulfillmentGroupSummary_T } from "../../../types/Shipping";
import ExtraLargeDialog from "../../../components/UI/ExtraLargeDialog";

interface ShowActiveFGsBtnProps {
  dateStart: string;
  dateEnd: string;
}

const defaultGridState: GridInitialState = {
  filter: { filterModel: { items: [] } },
  sorting: { sortModel: [{ field: "id", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

// Helper component for delivery IDs with copy button
const DeliveryIdsCell: React.FC<{
  deliveryIds: number[] | null;
  emptyText?: string;
}> = ({ deliveryIds, emptyText = "None" }) => {
  const idsString = deliveryIds?.length ? deliveryIds.join(", ") : emptyText;

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <span>{idsString}</span>
      {deliveryIds?.length && (
        <GaneshCopyToClipboardBtn
          textToCopy={idsString}
          showButtonText={false}
          variant="text"
          sx={{
            minWidth: "30px",
            width: "30px",
            color: "gray",
            padding: "2px",
            marginLeft: "10px",
          }}
        />
      )}
    </div>
  );
};

const ShowActiveFGsBtn: React.FC<ShowActiveFGsBtnProps> = ({
  dateStart,
  dateEnd,
}) => {
  const { showError } = useError();
  const { apiRequest } = useApi();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  // Only fetch data when dialog is opened
  const {
    data: fgRecords,
    isLoading,
    error,
  } = useQuery<FulfillmentGroupSummary_T[]>({
    queryKey: ["active-fulfillment-groups", dateStart, dateEnd],
    queryFn: async () => {
      const params = new URLSearchParams({ dateStart, dateEnd });
      const response = await apiRequest<FulfillmentGroupSummary_T[]>(
        `/v1/fulfillment_groups/summary/active_in_period?${params.toString()}`,
        { method: "GET" }
      );

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || [];
    },
    enabled: dialogOpen, // Only fetch when dialog is open
  });

  useEffect(() => {
    if (error) {
      showError(
        `Error fetching fulfillment groups for ${dateStart} - ${dateEnd}: ${error.message}`
      );
    }
  }, [error, showError, dateStart, dateEnd]);

  const columns_def: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
    },
    {
      field: "name",
      headerName: "Fulfillment Group",
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          to={`https://kuebiko.dataplor.com/customers/${params.row.customer_id}/fulfillment-groups/${params.row.id}/data`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {params.row.name}
          <RiExternalLinkLine size="14" />
        </Link>
      ),
    },
    {
      field: "customer_name",
      headerName: "Customer",
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          to={`/customer/${params.row.customer_id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {params.row.customer_name}
          <RiExternalLinkLine size="14" />
        </Link>
      ),
    },
    { field: "count_deliveries", headerName: "# Deliveries", width: 100 },
    { field: "count_countries", headerName: "# Countries", width: 100 },
    {
      field: "count_completed",
      headerName: "# Deliveries Completed",
      width: 100,
    },
    {
      field: "delivery_ids",
      headerName: "Delivery IDs",
      width: 300,
      renderCell: (params: GridRenderCellParams) => (
        <DeliveryIdsCell deliveryIds={params.row.delivery_ids} />
      ),
    },
    {
      field: "incomplete_delivery_ids",
      headerName: "Incomplete Deliveries",
      width: 300,
      renderCell: (params: GridRenderCellParams) => (
        <DeliveryIdsCell
          deliveryIds={params.row.incomplete_delivery_ids}
          emptyText="All deliveries complete 🎉"
        />
      ),
    },
    {
      field: "countries",
      headerName: "Countries",
      width: 500,
      renderCell: (params: GridRenderCellParams) =>
        params.row.countries
          ?.map((item: { id: string; name: string }) => item.name)
          .join(", ") || "None",
    },
  ];

  const handleClickOpen = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return (
    <>
      <Button variant="contained" onClick={handleClickOpen}>
        Show Active Fulfillment Groups
      </Button>
      <ExtraLargeDialog open={dialogOpen} onClose={handleClose}>
        <DialogTitle>Fulfillment Groups</DialogTitle>
        <DialogContent>
          <GaneshDataGrid
            loading={isLoading}
            data={fgRecords || []}
            columns={columns_def}
            gridId="ActiveFGsGrid"
            defaultGridState={defaultGridState}
            customHeight="70vh"
            getRowHeight={() => "auto"}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </ExtraLargeDialog>
    </>
  );
};

export default ShowActiveFGsBtn;
